import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth-context";
import { useCart } from "../lib/cart-context";
import PageSEO from "../components/SEO";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const { refresh: refreshCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  // Priority: ?redirect=... query > location.state.from > home
  const redirectTo =
    params.get("redirect") || location.state?.from || "/";
  const reason = params.get("reason");
  const reasonMsg =
    reason === "ship"
      ? "Inicia sesión para continuar con tu envío."
      : reason === "shop"
        ? "Inicia sesión para continuar con tu compra."
        : null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      await refreshCart();
      toast.success("Sesión iniciada");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo iniciar sesión";
      setError(typeof msg === "string" ? msg : "Credenciales inválidas");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="mx-auto max-w-md px-6 py-16 sm:py-24"
    >
      <PageSEO
        title="Iniciar sesión"
        description="Accede a tu cuenta MercaGo para gestionar pedidos y envíos a los campamentos saharauis."
        canonical="https://mercago.one/entrar"
        noindex
      />
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-[#00953B] text-white grid place-items-center font-black">
            M
          </div>
          <span className="text-2xl font-black tracking-tight">
            Merca<span className="text-[#00953B]">Go</span>
          </span>
        </Link>
      </div>
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8">
        <h1 className="text-2xl font-black tracking-tight">Iniciar sesión</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Accede a tu cuenta MercaGo para ver tus pedidos y habituales.
        </p>
        {reasonMsg && (
          <div
            data-testid="login-reason-banner"
            className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm px-3 py-2"
          >
            {reasonMsg}
          </div>
        )}
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="mt-1 h-11"
              placeholder="tu@email.com"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-sm">Contraseña</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="mt-1 h-11"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-btn"
            className="w-full h-11 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white font-semibold"
          >
            {submitting ? "Entrando…" : "Iniciar sesión"}
          </Button>
        </form>
        <div className="mt-3 text-sm text-center">
          <Link
            to="/olvide-contrasena"
            data-testid="login-forgot-password-link"
            className="text-neutral-500 hover:text-[#00953B] hover:underline"
          >
            ¿Olvidaste tu contraseña?
          </Link>
        </div>
        <div className="mt-5 text-sm text-neutral-600 text-center">
          ¿No tienes cuenta?{" "}
          <Link
            to="/registro"
            data-testid="login-to-register-link"
            className="text-[#00953B] font-semibold hover:underline"
          >
            Crear cuenta
          </Link>
        </div>
      </div>
    </div>
  );
}
